/**
 * A minimal on-screen error surface. WebXR runs on a standalone headset where
 * there is no console to open, so an uncaught error is otherwise an invisible
 * freeze/crash. This paints the message + stack into a DOM overlay: if a crash
 * drops the browser out of the immersive session (the common case), the text is
 * right there on the 2D page to read back. Main-thread and worker errors both
 * route here.
 */
let overlay: HTMLElement | null = null;
let sceneSink: ((source: string, detail: string) => void) | null = null;

/**
 * Register an in-scene error surface (a 3D panel). The DOM overlay is invisible
 * inside an immersive XR session, so in the headset this is the only way the
 * player sees that something broke.
 */
export function setSceneErrorSink(sink: (source: string, detail: string) => void): void {
  sceneSink = sink;
}

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  const el = document.createElement("div");
  el.setAttribute("role", "alert");
  el.style.cssText = [
    "position:fixed",
    "left:0",
    "right:0",
    "bottom:0",
    "max-height:45vh",
    "overflow:auto",
    "margin:0",
    "padding:12px 16px",
    "background:rgba(120,10,10,0.94)",
    "color:#fff",
    "font:13px/1.4 ui-monospace,Menlo,Consolas,monospace",
    "white-space:pre-wrap",
    "z-index:2147483647",
  ].join(";");
  document.body.appendChild(el);
  overlay = el;
  return el;
}

/** Show an error to the user (and log it). Safe to call repeatedly. */
export function reportError(source: string, err: unknown): void {
  const detail = err instanceof Error ? (err.stack ?? err.message) : String(err);
  // eslint-disable-next-line no-console
  console.error(`[${source}]`, err);
  try {
    const el = ensureOverlay();
    const line = document.createElement("div");
    line.textContent = `⚠ ${source}: ${detail}`;
    el.appendChild(line);
  } catch {
    /* DOM not available (e.g. worker context) — the console log above still fires */
  }
  if (sceneSink) {
    try {
      sceneSink(source, detail);
    } catch {
      /* never let the error surface throw */
    }
  }
}

/** Install global handlers for uncaught main-thread errors. Call once at boot. */
export function installErrorOverlay(): void {
  window.addEventListener("error", (e) => reportError("window", e.error ?? e.message));
  window.addEventListener("unhandledrejection", (e) => reportError("promise", e.reason));
}
